import { useContext, useMemo, useReducer, useState } from 'react'
import {
  WppButton,
  WppCheckbox,
  WppDivider,
  WppTypography,
  WppSelect,
  WppListItem,
  WppInlineMessage,
} from '@wppopen/components-library-react'
import formReducer, {
  handleError,
  initialState,
  resetForm,
  toggleCheckbox,
  updateInput,
} from 'reducers/CreateAndEditClientReducer/CreateAndEditClientReducer'
import type { WppCheckboxCustomEvent, WppSelectCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { CheckboxChangeEvent, SelectChangeEventDetail } from '@wppopen/components-library'
import type { ClientFormState } from './client-form'
import type { ClientsCreatePayload, ClientsDetailData } from '@contracts'
import type { FEApiError } from 'utils/errorHandling'
import { SettingsPageContext } from 'context/settingsPageContext/settingsPageContext'
import { useClientsToImport } from 'hooks/useClientsToImport'
import useCurrentUser from 'hooks/useCurrentUser'
import styles from './ImportClientForm.module.scss'

interface ImportClientFormProps {
  onCancelForm?: () => void
  onSubmitForm: (data: ClientsCreatePayload) => Promise<unknown>
  isAPILoading?: boolean
  clientData?: ClientsDetailData
}

export default function ImportClientForm({
  onCancelForm = () => {},
  onSubmitForm,
  isAPILoading,
  clientData,
}: ImportClientFormProps) {
  const [selectedClient, setSelectedClient] = useState<string>('')
  const { resetBanner } = useContext(SettingsPageContext)
  const { clientsToImport } = useClientsToImport()
  const { userName } = useCurrentUser()

  const selectedClientData = !!clientData ? clientData : clientsToImport?.find(client => client.id === selectedClient)

  const initialData = useMemo<ClientFormState>(() => {
    if (!selectedClientData) return initialState
    return {
      id: selectedClientData.id!,
      client_name: {
        value: selectedClientData.client_name,
        error: undefined,
      },
      regional_standard_generation: selectedClientData.approval_required?.regional_standard_generation ?? true,
      retailer_adaption: selectedClientData.approval_required?.retailer_adaption ?? true,
      sku_adaption: selectedClientData.approval_required?.sku_adaption ?? true,
    }
  }, [selectedClientData])

  const [formReducerState, formReducerDispatch] = useReducer(formReducer, initialData)
  const { client_name, regional_standard_generation, retailer_adaption, sku_adaption } = formReducerState

  const handleSelectChange = (e: WppSelectCustomEvent<SelectChangeEventDetail>) => {
    const { value } = e.target
    setSelectedClient(value)
    const selected = clientsToImport?.find(client => client.id === value)
    if (selected) {
      formReducerDispatch(updateInput(selected.client_name, true))
    }
  }

  const handleCheckboxChange = (e: WppCheckboxCustomEvent<CheckboxChangeEvent>) => {
    const { name, checked } = e.target
    formReducerDispatch(toggleCheckbox(name as string, checked))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    formReducerDispatch(handleError(''))

    if (!selectedClientData || !selectedClientData.id) {
      formReducerDispatch(handleError('Please select a client'))
      return
    }

    if (JSON.stringify(initialData) === JSON.stringify(formReducerState)) {
      formReducerDispatch(handleError('No changes were made'))
      return
    }

    const data: ClientsCreatePayload = {
      id: selectedClientData.id,
      client_name: selectedClientData.client_name,
      approval_required: {
        regional_standard_generation,
        retailer_adaption,
        sku_adaption,
      },
      update_info: {
        user_name: userName,
      },
    }

    try {
      await onSubmitForm(data)
    } catch (error) {
      const apiError = error as FEApiError
      const isErrorArray = !!apiError?.errors?.length
      formReducerDispatch(handleError(isErrorArray ? apiError.errors![0].message : apiError.message))
    }
  }

  const resetFormState = () => {
    setSelectedClient('')
    formReducerDispatch(resetForm(initialState))
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault()
    resetFormState()
    onCancelForm()
    resetBanner()
  }

  return (
    <div className={styles.form_warper} data-testid="form-wrapper">
      <form onSubmit={handleSubmit} data-testid="import-client-form" id="import-client-form">
        <WppSelect
          className={styles.select}
          placeholder="Please select"
          labelConfig={{
            text: 'Client',
          }}
          required
          name="client_name"
          value={!!clientData ? clientData.id : selectedClient}
          onWppChange={handleSelectChange}
          data-testid="client-select"
          disabled={!clientsToImport?.length || !!clientData}
        >
          {!!clientData && !!clientData.client_logo_url && (
            <img
              height={20}
              src={clientData.client_logo_url}
              alt={clientData.client_name}
              data-testid="client-logo"
              slot="icon-start"
            />
          )}

          {!!clientData ? (
            <WppListItem value={clientData.id} data-testid="region-options">
              <p slot="label">{clientData.client_name}</p>
            </WppListItem>
          ) : (
            clientsToImport?.map(({ id, client_name }) => (
              <WppListItem key={id} value={id}>
                <p slot="label">{client_name}</p>
              </WppListItem>
            ))
          )}
        </WppSelect>
        <WppDivider className={styles.divider} />

        <WppTypography type="s-body" tag="p" className={styles.disclaimer}>
          Configure which content types require internal approvals for this client:
        </WppTypography>
        <div className={styles.checkboxes_container}>
          <WppCheckbox
            labelConfig={{ text: 'Regional standard content (generation)' }}
            name="regional_standard_generation"
            required
            checked={regional_standard_generation}
            onWppChange={handleCheckboxChange}
            data-testid="regional-standard-generation-checkbox"
          />
          <WppCheckbox
            labelConfig={{ text: 'Retailer content (adaption)' }}
            name="retailer_adaption"
            required
            checked={retailer_adaption}
            onWppChange={handleCheckboxChange}
            data-testid="retailer-adaption-checkbox"
          />
          <WppCheckbox
            labelConfig={{ text: 'SKU content (adaption)' }}
            name="sku_adaption"
            required
            checked={sku_adaption}
            onWppChange={handleCheckboxChange}
            data-testid="sku-adaption-checkbox"
          />
        </div>

        <div className={styles.actions}>
          <div>
            <WppButton variant="secondary" onClick={handleCancel} data-testid="cancel-cta">
              Cancel
            </WppButton>
            <WppButton
              type="submit"
              variant="primary"
              data-testid="import-cta"
              loading={isAPILoading}
              disabled={!selectedClientData}
            >
              {!!clientData ? 'Save' : 'Import'}
            </WppButton>
          </div>
          {!!client_name.error && (
            <WppInlineMessage size="s" className={styles.error} message={client_name.error} type="error" />
          )}
        </div>
      </form>
    </div>
  )
}
